import { left, marginLeft, marginRight, translate } from '@/utils/directions';
import {
  colorAccent,
  colorAccentHover,
  colorBackgroundBody,
  colorBlue,
  colorBlueHover,
  colorDarkRed,
  colorDustyWhite,
  colorGray,
  colorGreen,
  colorLightGray,
  colorLightRed,
  colorRed,
  colorRedHover,
  colorText,
  colorWhite,
  colorYellow,
  colorYellowHover,
} from '@/utils/palette';
import { darken } from 'polished';
import {
  Button as BootstrapButton,
  ButtonGroup as BootstrapButtonGroup,
  ButtonToolbar as BootstrapButtonToolbar,
  Dropdown as BootstrapDropdown,
} from 'react-bootstrap';
import styled from 'styled-components';

const getColor = variant => {
	switch (true) {
		case variant?.includes('primary'):
		case variant?.includes('unverified'):
			return colorBlue;
		case variant?.includes('Low'):
		case variant?.includes('success'):
			return colorAccent;
		case variant?.includes('warning'):
		case variant?.includes('Medium'):
		case variant?.includes('lowRisk'):
			return colorYellow;
		case variant?.includes('danger'):
		case variant?.includes('High'):
			return colorRed;
		case variant?.includes('mediumRisk'):
			return colorLightRed;
		case variant?.includes('None'):
		case variant?.includes('none'):
		case variant?.includes('Success'):
			return colorGreen;
		case variant?.includes('Critical'):
		case variant?.includes('critical'):
			return colorDarkRed;
		default:
			return colorLightGray;
	}
};

const getBackgroundColor = variant => {
	if (!variant || variant?.includes('outline')) {
		return null;
	}

	return getColor(variant);
};

const getHoverColor = variant => {
	if (variant?.includes('outline')) {
		return getColor(variant);
	}

	switch (true) {
		case variant?.includes('secondary'):
			return null;
		case variant?.includes('primary'):
			return colorBlueHover;
		case variant?.includes('success'):
			return colorAccentHover;
		case variant?.includes('warning'):
			return colorYellowHover;
		case variant?.includes('danger'):
			return colorRedHover;
		case variant?.includes('critical'):
		case variant?.includes('Critical'):
			return colorDarkRed;
		default:
			return null;
	}
};

const getHoverTextColor = variant => {
	if (!variant || variant?.includes('secondary')) {
		return colorGray;
	}

	return colorWhite;
};

const getTextColor = variant => {
	if (!variant?.includes('outline')) {
		return variant?.includes('secondary') ? colorGray : colorWhite;
	}

	return variant?.includes('secondary') ? colorText : getColor(variant);
};

const getPaddings = size => {
	switch (size) {
		case 'iconButton':
			return '7px 12px';
		case 'xs':
			return '2px 10px';
		case 'sm':
			return '5px 25px';
		case 'lg':
			return '12px 25px';
		default:
			return '10px 25px';
	}
};

const getBorderRadius = (rounded, squared) => {
	switch (true) {
		case rounded:
			return '30px';
		case squared:
			return 0;
		default:
			return '5px';
	}
};

export const Button = styled(BootstrapButton)`
	border-radius: ${props => getBorderRadius(props.rounded, props.squared)};
	padding: ${props => getPaddings(props.size)};
	transition: all 0.4s;
	font-size: ${props => (props.size === 'xs' ? '10px' : '14px')};
	position: relative;
	overflow: hidden;
	z-index: 0;
	${marginRight}: 15px;
	background-color: ${props =>
		getBackgroundColor(props.variant) || 'transparent'};
	border: 1px solid;
	border-color: ${props => getColor(props.variant)};
	color: ${props => getTextColor(props.variant)};
	display: inline-flex;
	align-items: center;
	justify-content: center;
	text-align: center;

	&:last-child {
		margin-right: 0;
		${marginRight}: 0;
	}

	&:before {
		position: absolute;
		height: 0;
		width: 0;
		border-radius: 50%;
		background-color: ${colorAccent};
		transition:
			width 0.3s ease-in-out,
			height 0.3s ease-in-out;
		z-index: -1;
		content: '';
		top: 0;
		transform: ${translate};
		${left}: 0;
		background-color: ${props =>
			getHoverColor(props.variant) || darken(0.1, colorLightGray)};
	}

	span {
		transition: all 0.3s;
		font-weight: 500;
		color: ${props => getTextColor(props.variant)};
	}

	svg {
		height: 14px;
		width: 14px;
		transition: all 0.3s;
		fill: ${props => getTextColor(props.variant)};

		${props =>
			props.space &&
			`
      margin-right: 5px;
    `}

		&:not(:last-child) {
			${marginRight}: 5px;
		}

		&:not(:first-child) {
			${marginLeft}: 5px;
		}
	}

	&:hover,
	&:focus,
	&:active,
	&:active:focus {
		outline: none;
		box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
		transform: translateY(-1px);
		color: ${props => getHoverTextColor(props.variant)};
		border-color: ${props =>
			getHoverColor(props.variant) || darken(0.1, colorLightGray)};
		background-color: ${props =>
			getBackgroundColor(props.variant) || 'transparent'};

		&:before {
			height: 500%;
			width: 225%;
		}

		span {
			color: ${props => getHoverTextColor(props.variant)};
		}

		svg {
			fill: ${props => getHoverTextColor(props.variant)};
		}
	}

	&:focus,
	&:active,
	&:active:focus {
		&:before {
			transition: all 0s;
		}
	}

	&:disabled {
		background-color: ${colorBackgroundBody};
		border-color: ${colorBackgroundBody};
		color: ${colorDustyWhite};
		pointer-events: none;

		span {
			color: ${colorDustyWhite};
		}

		svg {
			fill: ${colorDustyWhite};
		}
	}

	@media (max-width: 645px) {
		margin-bottom: 10px;
	}

	@media screen and (min-width: 576px) {
		${props =>
			props.collapse &&
			`
      display :none
    `}
	}
`;
export const DropdownButton = styled(BootstrapDropdown)`
	// border-radius: ${props => getBorderRadius(props.rounded, props.squared)};
	// padding: ${props => getPaddings(props.size)};
	// transition: all 0.4s;
	font-size: ${props => (props.size === 'xs' ? '10px' : '14px')};
	position: relative;
	// overflow: hidden;
	// z-index: 0;
	${marginRight}: 15px;
	background-color: ${props =>
		getBackgroundColor(props.variant) || 'transparent'} !important;
	color: ${props => getTextColor(props.variant)};
	display: inline-flex;
	align-items: center;
	justify-content: center;
	text-align: center;

	&:last-child {
		margin-right: 0;
		${marginRight}: 0;
	}

	&:before {
		position: absolute;
		height: 0;
		width: 0;
		// border-radius: 50%;
		background-color: ${colorAccent};
		transition:
			width 0.3s ease-in-out,
			height 0.3s ease-in-out;
		z-index: -1;
		content: '';
		top: 0;
		transform: ${translate};
		${left}: 0;
		background-color: ${props =>
			getHoverColor(props.variant) || darken(0.1, colorLightGray)};
	}

	span {
		transition: all 0.3s;
		font-weight: 500;
		color: ${props => getTextColor(props.variant)};
	}

	svg {
		height: 14px;
		width: 14px;
		transition: all 0.3s;
		fill: ${props => getTextColor(props.variant)};

		${props =>
			props.space &&
			`
      margin-right: 5px;
    `}

		&:not(:last-child) {
			${marginRight}: 5px;
		}

		&:not(:first-child) {
			${marginLeft}: 5px;
		}
	}

	&:hover,
	&:active,
	&:active:focus {
		outline: none;
		// box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
		// transform: translateY(-1px);
		color: ${props => getHoverTextColor(props.variant)};
		// border-color: ${props =>
			getHoverColor(props.variant) || darken(0.1, colorLightGray)};
		// background-color: ${props =>
			getBackgroundColor(props.variant) || 'transparent'};

		//&:before {
		//   height: 500%;
		//   width: 225%;
		// }

		span {
			color: ${props => getHoverTextColor(props.variant)};
		}

		svg {
			fill: ${props => getHoverTextColor(props.variant)};
		}
	}

	&:focus,
	&:active,
	&:active:focus {
		&:before {
			transition: all 0s;
		}
	}

	&:disabled {
		background-color: ${colorBackgroundBody};
		border-color: ${colorBackgroundBody};
		color: ${colorDustyWhite};
		pointer-events: none;

		span {
			color: ${colorDustyWhite};
		}

		svg {
			fill: ${colorDustyWhite};
		}
	}

	@media (max-width: 645px) {
		margin-bottom: 10px;
	}

	@media screen and (min-width: 576px) {
		${props =>
			props.collapse &&
			`
      display :none
    `}
	}
`;

export const DropdownToggle = styled(BootstrapDropdown.Toggle)`
	border-radius: 5px;
	// padding: ${props => getPaddings(props.size)};
	padding: 5px 10px;
	background-color: ${props =>
		getBackgroundColor(props.variant) || 'transparent'};
	// color: ${props => getTextColor(props.variant)};
	font-size: ${props => (props.size === 'xs' ? '10px' : '14px')};
	// font-weight: 500;
	// transition: all 0.3s;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	text-align: center;
	border: none;
	height: 30px !important;

	&:hover,
  // &:focus,
  &:active {
		background-color: ${props =>
			getHoverColor(props.variant) || darken(0.1, colorLightGray)};
		color: ${props => getHoverTextColor(props.variant)};
		// border-color: ${props =>
			getHoverColor(props.variant) || darken(0.1, colorLightGray)};
	}

	&:disabled {
		background-color: ${colorBackgroundBody};
		border-color: ${colorBackgroundBody};
		color: ${colorDustyWhite};
		pointer-events: none;
	}
`;
export const DropdownMenu = styled(BootstrapDropdown.Menu)`
	background-color: ${colorBackgroundBody};
`;
export const DropdownItem = styled(BootstrapDropdown.Item)`
	color: ${colorText} !important;
	font-size: 12px !important;

	&:hover,
  // &:focus,
  &:active {
		background-color: ${props =>
			getHoverColor(props.variant) || darken(0.1, colorLightGray)};
		color: #555 !important;
		// border-color: ${props =>
			getHoverColor(props.variant) || darken(0.1, colorLightGray)};
	}
`;

export const ButtonToolbar = styled(BootstrapButtonToolbar)`
	margin-top: 15px;
	margin-bottom: 10px;

	& > * {
		${marginRight}: 15px;

		&:last-child {
			margin-right: 0;
		}
	}

	&:first-child {
		margin-top: 0;
	}

	&:last-child {
		margin-bottom: -10px;
	}

	${props =>
		props.centered &&
		`
    & > * {
      &,
      &:last-child,
      &:first-child {
        margin-right: auto;
        margin-left: auto;
      }
    }
  `}

	${props =>
		props.form &&
		`
    margin-left:20px;`}
`;

export const ButtonGroup = styled(BootstrapButtonGroup)`
	border-radius: 5px;
	margin-bottom: -10px;

	.btn {
		padding: 10px 15px;
		font-weight: 500;
		${marginRight}: 0;
	}

	${props =>
		props.justified &&
		`
    display: flex;
    justify-content: space-between;
    width: 100%;

    .btn {
      width: 100%;
    }
  `}

	${props =>
		props.icons &&
		`
    .btn {
      padding: 7px 8px;
      line-height: 14px;
    }
  `}

  &.open .dropdown-toggle {
		box-shadow: none;
	}
`;

export const ButtonWrap = styled.div`
	display: flex;
	justify-content: flex-end;
	gap: 2;
`;

export const ButtonTitleWrap = styled.div`
	display: flex;
	align-items: end;
	justify-content: flex-end;
`;

export const DefaultButtonTitleWrap = styled.div`
	display: flex;
	justify-content: space-between;
`;

export const PanelButton = styled.div`
	display: flex;
	float: right;
	margin-top: -50px;
`;

export const CenteredButtons = styled.div`
	display: flex;
	justify-content: center;
	align-items: center;
	flex-wrap: wrap;
`;
